import { Box, Divider, Group, Input, Select, Tooltip } from '@byecode/ui'
import type { FlowNode, NodeTypes, SendMsgDingTalkRobotConfig, SendMsgDingTalkRobotConfigMsgType } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getUpstreamNodesOptions, ListItem4ByecodeUi, nodeTypeOptions, useAtomData } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import { GroupRequired } from '../../Common/GroupRequired'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import { SEND_MSG_DINGTALK_OPTIONS } from '../../constant'
import * as CM from '../../styles'
import { LinkMsgConfig } from './LinkMsgConfig'
import { RichTextMsgConfig } from './RichTextMsgConfig'
import * as SC from './styles'
import { TextMsgConfig } from './TextMsgConfig'

interface DingTalkRobotActionConfigureProps {
    allParentNodes: FlowNode[]
    nodeType?: NodeTypes
    actionTypeSwitcher?: React.ReactNode
    value: SendMsgDingTalkRobotConfig
    onChange: (value: SendMsgDingTalkRobotConfig) => void
}

export const DingTalkRobotActionConfigure: React.FC<DingTalkRobotActionConfigureProps> = ({
    allParentNodes,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const workflowList = useAtomData(workflowListAtom)
    const msgType = value.msgtype

    const userOption = useGetUserRecordOptions()

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes) || [],
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const differentConfigEle = useMemo(() => {
        switch (msgType) {
            case 'text': {
                return (
                    <TextMsgConfig
                        upstreamOptions={textUpstreamOptions}
                        userOption={userOption}
                        dataSourceList={dataSourceList}
                        value={value}
                        onChange={onChange}
                    />
                )
            }

            case 'markdown': {
                return (
                    <RichTextMsgConfig
                        upstreamOptions={textUpstreamOptions}
                        userOption={userOption}
                        dataSourceList={dataSourceList}
                        value={value}
                        onChange={onChange}
                    />
                )
            }

            case 'link': {
                return (
                    <LinkMsgConfig
                        upstreamOptions={textUpstreamOptions}
                        userOption={userOption}
                        dataSourceList={dataSourceList}
                        value={value}
                        onChange={onChange}
                    />
                )
            }

            default: {
                return null
            }
        }
    }, [dataSourceList, msgType, onChange, textUpstreamOptions, userOption, value])

    const dingTalkAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'DING_TALK_ROBOT'), [otherPlatformList])

    if (!dingTalkAccount) {
        return <UnitNoAuth type="DING_TALK_ROBOT" />
    }

    return (
        <CM.Container>
            {actionTypeSwitcher ? (
                <>
                    <Group label="动作配置">
                        <ListItem4ByecodeUi>
                            <CM.FormItemLabelWrapper>
                                <CM.FormItemLabel>类型</CM.FormItemLabel>
                            </CM.FormItemLabelWrapper>
                            <CM.FormItemContent>{actionTypeSwitcher}</CM.FormItemContent>
                        </ListItem4ByecodeUi>
                    </Group>
                    <Divider />
                </>
            ) : (
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <CM.FormItemLabelWrapper>
                            <CM.FormItemLabel>类型</CM.FormItemLabel>
                        </CM.FormItemLabelWrapper>
                        <CM.FormItemContent>
                            <Select disabled value={nodeType} options={nodeTypeOptions} />
                        </CM.FormItemContent>
                    </ListItem4ByecodeUi>
                </Group>
            )}
            {/* <ActionAuthIntegrateCard type="DING_TALK_ROBOT" />
            <Divider color="var(--color-gray-200)" /> */}
            <GroupRequired label="钉钉机器人配置" required>
                <Box px={8}>
                    <SC.RowList>
                        <SC.RowItem>
                            <SC.RowItemLeft>
                                <SC.RowText>Webhook地址</SC.RowText>
                                <SC.Text color="red" style={{ paddingLeft: 4, marginRight: 4 }}>
                                    *
                                </SC.Text>
                                <Tooltip
                                    title={
                                        <SC.TooltipContent>
                                            选择一个钉钉内部员工群，右上角“群设
                                            <br />
                                            置”&gt;群管理&gt;机器人&gt;添加机器人&gt;添加“自定义机
                                            <br />
                                            器人”，添加成功后，复制 Webhook 地址
                                        </SC.TooltipContent>
                                    }
                                >
                                    <SC.Icon type="Question" size={16} color="var(--color-gray-400)" />
                                </Tooltip>
                            </SC.RowItemLeft>
                            <SC.RowItemRight>
                                <Input
                                    value={value.webhook}
                                    style={{ width: 180 }}
                                    onChange={e => onChange({ ...value, webhook: e.target.value })}
                                    placeholder="请粘贴"
                                />
                            </SC.RowItemRight>
                        </SC.RowItem>

                        <SC.RowItem>
                            <SC.RowItemLeft>
                                <SC.RowText>加签密钥</SC.RowText>
                                <SC.Text color="red" style={{ paddingLeft: 4, marginRight: 4 }}>
                                    *
                                </SC.Text>
                                <Tooltip
                                    title={
                                        <SC.TooltipContent>
                                            在创建钉钉自定义机器人时
                                            <br />
                                            的加密设置
                                        </SC.TooltipContent>
                                    }
                                >
                                    <SC.Icon type="Question" size={16} color="var(--color-gray-400)" />
                                </Tooltip>
                            </SC.RowItemLeft>
                            <SC.RowItemRight>
                                {/* <Input placeholder="请粘贴" /> */}
                                <Input
                                    autoComplete="new-password"
                                    style={{ width: 180 }}
                                    type="password"
                                    value={value.secret}
                                    onChange={e => onChange({ ...value, secret: e.target.value })}
                                    placeholder="请输入"
                                />
                            </SC.RowItemRight>
                        </SC.RowItem>

                        <SC.RowItem>
                            <SC.RowItemLeft>
                                <SC.RowText>执行操作</SC.RowText>
                            </SC.RowItemLeft>
                            <SC.RowItemRight>
                                <Select
                                    styles={{
                                        root: {
                                            width: 180
                                        }
                                    }}
                                    options={SEND_MSG_DINGTALK_OPTIONS}
                                    value={value.msgtype}
                                    onChange={msgtype => onChange({ ...value, msgtype: msgtype as SendMsgDingTalkRobotConfigMsgType })}
                                />
                            </SC.RowItemRight>
                        </SC.RowItem>
                    </SC.RowList>
                </Box>
            </GroupRequired>
            {msgType && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label="配置参数">
                        <Box px={8}>{differentConfigEle}</Box>
                    </Group>
                </>
            )}
        </CM.Container>
    )
}
